import Dynamic from '../dynamic/Dynamic'
import { Device } from '../helpers/Device'
import PrebidFacade from '../prebidFacade/PrebidFacade'
import { Slot } from '../slots/Slot'
import SlotFactory from '../slots/SlotFactory'

export interface IHintConfig {
  desktopSelector: string
  mobileSelector: string
  slot?: SlotClass
  slotFactory: SlotFactory
  prebidFacade: PrebidFacade
}
export class Hint {
  /** This attribute acts as a mutex.
   * It is added onto a hint div once
   * a slot is inserted.
   */
  private static readonly INSERTED_SLOT_ATTRIBUTE = 'data-hint-slot-inserted'
  private static readonly SLOT_TYPE_ATTRIBUTE = 'data-slot'
  private static readonly SIZES_ATTRIBUTE = 'data-hint-slot-sizes'

  private static readonly validSizeOverrideDeviceValues = ['m', 't', 'd']

  private constructor() {
    throw Error('Singleton class. Do not instantiate.')
  }

  static getSizeOverride(dataSizeString: string | null): AdSizes {
    const sizeArray =
      (dataSizeString && dataSizeString.replace(/\s/g, '').split(',')) || []
    const keyedSizes = sizeArray.reduce(
      (accumulatedKeys, currentSizeString) => {
        const possibleDeviceKey = currentSizeString[0]
        const sizeStringIsDeviceKeyed = this.validSizeOverrideDeviceValues.includes(
          possibleDeviceKey
        )
        const deviceKey = sizeStringIsDeviceKeyed ? possibleDeviceKey : 'all'
        const sizeString = sizeStringIsDeviceKeyed
          ? currentSizeString.slice(1)
          : currentSizeString

        const [height, width] = sizeString
          .split('x')
          .map((value) => parseInt(value, 10))
        accumulatedKeys[deviceKey].push([height, width])

        return accumulatedKeys
      },
      {
        all: [],
        m: [],
        t: [],
        d: []
      }
    )

    const overridenSizes = keyedSizes.all
    if (Device.isMobile) {
      return overridenSizes.concat(keyedSizes.m)
    } else if (Device.isTablet) {
      return overridenSizes.concat(keyedSizes.t)
    } else {
      return overridenSizes.concat(keyedSizes.d)
    }
  }

  static getHintParagraphs(
    config: Pick<IHintConfig, 'mobileSelector' | 'desktopSelector'>
  ): Array<Element> {
    const hintParagraphs = Array.from(
      document.querySelectorAll(
        Device.isMobileOrTablet ? config.mobileSelector : config.desktopSelector
      )
    )

    return hintParagraphs
  }

  static getTypeHintParagraphs(
    config: Pick<IHintConfig, 'mobileSelector' | 'desktopSelector' | 'slot'>
  ): Array<Element> {
    const classSelector = Device.isMobileOrTablet
      ? config.mobileSelector
      : config.desktopSelector
    const dataSelector = config.slot
      ? `[${Hint.SLOT_TYPE_ATTRIBUTE}="${config.slot.toLowerCase()}"]`
      : ''
    const hintParagraphs = Array.from(
      document.querySelectorAll(`${dataSelector}${classSelector}`)
    )

    return hintParagraphs
  }

  static addSlotsToHints(config: IHintConfig): boolean {
    const hintParagraphs = Hint.getHintParagraphs(config)
    let didAdSlot = false
    if (!hintParagraphs || !hintParagraphs.length) {
      return didAdSlot
    }

    for (const paragraph of hintParagraphs) {
      if (paragraph.getAttribute(Hint.INSERTED_SLOT_ATTRIBUTE) !== 'true') {
        didAdSlot = true

        const slot = config.slot
          ? config.slot
          : (capitalize(
              paragraph.getAttribute(Hint.SLOT_TYPE_ATTRIBUTE)
            ) as SlotClass)

        const sizeOverride = paragraph.getAttribute(Hint.SIZES_ATTRIBUTE)
        const newSizes = Hint.getSizeOverride(sizeOverride)
        const isEmptyHint = sizeOverride && !newSizes.length

        let newSlot: Slot | null = null

        if (!isEmptyHint) {
          newSlot = Dynamic.insertSlot(
            paragraph as HTMLElement,
            slot,
            config.slotFactory,
            undefined,
            config.prebidFacade
          )
        }

        if (newSlot && newSizes.length) {
          newSlot.sizes = newSizes
        }

        // If the slot is not lazy, we need to request bids for it.
        if (newSlot && !newSlot.lazy) {
          config.prebidFacade.requestBids([newSlot], newSlot.timeout.value)
        }
        paragraph.setAttribute(Hint.INSERTED_SLOT_ATTRIBUTE, 'true')
      }
    }

    return didAdSlot
  }
}

function capitalize(str: string | null): string | null {
  if (!str) {
    return str
  }

  return str.charAt(0).toUpperCase() + str.slice(1)
}
